import React, { useEffect } from 'react';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { showAlert } from '../components/service/AlertService';
import { motion } from 'framer-motion';
import Footer from '../components/layout/Footer';

/* Variants para animaciones */
const benefitContainerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.3
    }
  }
};

const benefitItemVariants = {
  hidden: { opacity: 0, y: 30, scale: 0.9 },
  visible: { opacity: 1, y: 0, scale: 1, transition: { type: "spring", stiffness: 100, damping: 20 } }
};

const statContainerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.4
    }
  }
};

const statItemVariants = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: { opacity: 1, scale: 1, transition: { type: "spring", stiffness: 120 } }
};

const stepContainerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
      delayChildren: 0.3
    }
  }
};

const stepItemVariants = {
  hidden: { opacity: 0, x: -50 },
  visible: { opacity: 1, x: 0, transition: { type: "spring", stiffness: 80 } }
};

/* ─── Google Fonts ───────────────────────────────────────────────── */
if (!document.getElementById('aecy-fonts')) {
  const link = document.createElement('link');
  link.id = 'aecy-fonts';
  link.rel = 'stylesheet';
  link.href = 'https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@300;400;500&family=Space+Mono:wght@400;700&display=swap';
  document.head.appendChild(link);
}

/* ─── CSS global ─────────────────────────────────────────────────── */
const globalStyle = `
  :root {
    --aecy-black:      #0d0f12;
    --aecy-surface:    #161a20;
    --aecy-surface-2:  #1e232b;
    --aecy-border:     rgba(255,255,255,0.07);
    --aecy-border-acc: rgba(59,130,246,0.35);
    --aecy-white:      #e8eaf0;
    --aecy-white-dim:  rgba(232,234,240,0.55);
    --aecy-accent:     #3b82f6;
    --aecy-accent-dim: rgba(59,130,246,0.12);
    --aecy-accent-hov: #2563eb;
    --aecy-gray:       #6b7280;
  }
  @keyframes aecy-ticker {
    from { transform: translateX(0); }
    to   { transform: translateX(-50%); }
  }
  .aecy-ticker-wrap  { overflow: hidden; white-space: nowrap; width: 100%; }
  .aecy-ticker-inner { display: inline-flex; gap: 3rem; }

  .aecy-benefit-item  { background: var(--aecy-surface); transition: background 0.2s; }
  .aecy-benefit-item:hover { background: var(--aecy-surface-2) !important; }
  .aecy-stat-box:hover      { border-color: var(--aecy-border-acc) !important; }
  .aecy-team-card:hover     { border-color: var(--aecy-border-acc) !important; }
  .aecy-study-card:hover    { border-color: var(--aecy-border-acc) !important; }
  .aecy-collab-name { color: rgba(232,234,240,0.18); transition: color 0.2s; cursor: default; font-family: 'Bebas Neue', sans-serif; }
  .aecy-collab-name:hover   { color: var(--aecy-accent) !important; }
  .aecy-contact-link:hover  { color: var(--aecy-accent) !important; }
  .aecy-btn-primary:hover   { background: var(--aecy-accent-hov) !important; transform: translateY(-2px) !important; }
  .aecy-btn-ghost:hover     { color: var(--aecy-white) !important; }
  .aecy-cta-btn:hover       { background: var(--aecy-accent-hov) !important; }

  .aecy-first-section { padding-top: 5rem !important; }

  /* Responsive breakpoints */
  @media (max-width: 900px) {
    .aecy-hero-img          { display: none !important; }
    .aecy-two-col,
    .aecy-how-grid,
    .aecy-contact-grid      { grid-template-columns: 1fr !important; gap: 2.5rem !important; }
    .aecy-three-col         { grid-template-columns: 1fr !important; }
    .aecy-studies-hdr       { flex-direction: column !important; align-items: flex-start !important; }
    .aecy-stats-grid        { grid-template-columns: 1fr 1fr !important; }
    .aecy-team-row          { flex-direction: column !important; }
    .aecy-group-grid        { grid-template-columns: 1fr !important; }
    .aecy-section           { padding: 4rem 1.5rem !important; }
    .aecy-hero-left         { padding: 6rem 1.5rem 3.5rem !important; justify-content: flex-start !important; }
  }
  @media (min-width: 600px) and (max-width: 900px) {
    .aecy-three-col { grid-template-columns: 1fr 1fr !important; }
  }
`;

if (!document.getElementById('aecy-style')) {
  const s = document.createElement('style');
  s.id = 'aecy-style';
  s.textContent = globalStyle;
  document.head.appendChild(s);
}

/* ─── Tipografía ─────────────────────────────────────────────────── */
const f = {
  display: "'Bebas Neue', sans-serif",
  body:    "'DM Sans', sans-serif",
  mono:    "'Space Mono', monospace",
};

const Tag = ({ children, center }) => (
  <p style={{
    fontFamily: f.mono, fontSize: '0.62rem', letterSpacing: '0.22em',
    textTransform: 'uppercase', color: 'var(--aecy-accent)',
    margin: '0 0 1.2rem', textAlign: center ? 'center' : 'left',
  }}>{children}</p>
);

const Title = ({ children, center }) => (
  <h2 style={{
    fontFamily: f.display, fontSize: 'clamp(2.4rem, 4.5vw, 4.8rem)',
    lineHeight: 1, margin: '0 0 1.5rem', color: 'var(--aecy-white)',
    textAlign: center ? 'center' : 'left',
  }}>{children}</h2>
);

/* ══════════════════════════════════════════════════════════════════
   COMPONENTE HOME
══════════════════════════════════════════════════════════════════ */
const Home = () => {
  const { t } = useTranslation();

  /* Evitar scroll horizontal residual */
  useEffect(() => {
    document.body.style.overflowX = 'hidden';
    return () => { document.body.style.overflowX = ''; };
  }, []);

  /* Token expirado */
  const tokenEnd = localStorage.getItem('tokenEnd');
  if (tokenEnd) { showAlert('TOKEN_END'); localStorage.removeItem('tokenEnd'); }

  /* ── Data ── */
  const ticker = [
    t('home.ticker.biomechanics'), t('home.ticker.aero'),
    t('home.ticker.cda'),          t('home.ticker.velodrome'),
    t('home.ticker.kinematics'),   t('home.ticker.kinetics'),
  ];

  const benefits = [
    { icon: '🚴', title: t('home.benefits.comfort.title'),    desc: t('home.benefits.comfort.desc') },
    { icon: '🛡️', title: t('home.benefits.injury.title'),     desc: t('home.benefits.injury.desc') },
    { icon: '💨', title: t('home.benefits.aero.title'),       desc: t('home.benefits.aero.desc') },
    { icon: '🎛️', title: t('home.benefits.components.title'), desc: t('home.benefits.components.desc') },
    { icon: '⚡', title: t('home.benefits.faster.title'),     desc: t('home.benefits.faster.desc') },
    { icon: '📊', title: t('home.benefits.data.title'),       desc: t('home.benefits.data.desc') },
  ];

  const stats = [
    { num: 'CdA',  label: t('home.about.stats.cda') },
    { num: '5h',   label: t('home.about.stats.study') },
    { num: '2',    label: t('home.about.stats.experts') },
    { num: '360°', label: t('home.about.stats.analysis') },
  ];

  const steps = [
    { title: t('home.how.step1.title'), desc: t('home.how.step1.desc') },
    { title: t('home.how.step2.title'), desc: t('home.how.step2.desc') },
    { title: t('home.how.step3.title'), desc: t('home.how.step3.desc') },
    { title: t('home.how.step4.title'), desc: t('home.how.step4.desc') },
  ];

  const studies = [
    {
      type: t('home.studies.biomech.type'), name: t('home.studies.biomech.name'),
      duration: t('home.studies.biomech.duration'), featured: false,
      prices: [{ label: t('home.studies.one_bike'), value: '275€' }, { label: t('home.studies.two_bikes'), value: '365€' }],
    },
    {
      type: t('home.studies.aero.type'), name: t('home.studies.aero.name'),
      nameAccent: t('home.studies.aero.nameAccent'),
      duration: t('home.studies.aero.duration'), featured: false,
      prices: [{ label: t('home.studies.one_bike'), value: '295€' }, { label: t('home.studies.two_bikes'), value: '365€' }],
    },
    {
      type: t('home.studies.complete.type'), name: t('home.studies.complete.name'),
      nameAccent: t('home.studies.complete.nameAccent'),
      duration: t('home.studies.complete.duration'), featured: true,
      badge: t('home.studies.recommended'), note: t('home.studies.best_value'),
      prices: [{ label: t('home.studies.one_bike'), value: '399€' }],
    },
  ];

  /* ── Estilos de sección reutilizables ── */
  const section = (bg) => ({
    width: '100%', boxSizing: 'border-box',
    padding: '7rem 4rem', background: bg,
  });

  /* ══ RENDER ═══════════════════════════════════════════════════ */
  return (
    <Box sx={{
      width: '100%',
      background: 'var(--aecy-black)',
      color: 'var(--aecy-white)',
      fontFamily: f.body,
      overflowX: 'hidden',
    }}>

      {/* ── HERO ──────────────────────────────────────────────── */}
      <Box sx={{
        minHeight: { xs: 'auto', md: '60dvh' },
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
        background: 'var(--aecy-black)',
      }}>
        {/* Left */}
        <motion.div className="aecy-hero-left" style={{
          display: 'flex', flexDirection: 'column', justifyContent: 'center',
          padding: 'clamp(3rem,7vw,10rem) clamp(1.5rem,4vw,4rem) clamp(3rem,5vw,5rem)',
          zIndex: 2,
        }} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1.2, ease: "easeOut", delay: 0.5 }}>
          <p style={{ fontFamily: f.mono, fontSize: '0.65rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: 'var(--aecy-accent)', margin: '0 0 1.5rem' }}>
            {t('home.hero.tag')}
          </p>
          <h1 style={{
            fontFamily: f.display,
            fontSize: 'clamp(4rem, 8.5vw, 9.5rem)',
            lineHeight: 0.92, margin: '0 0 2rem', color: 'var(--aecy-white)',
          }}>
            {t('home.hero.title1')}<br />
            {t('home.hero.title2')}<br />
            <span style={{ color: 'var(--aecy-accent)' }}>{t('home.hero.title3')}</span>
          </h1>
          <p style={{ fontSize: '1rem', color: 'var(--aecy-white-dim)', maxWidth: 420, lineHeight: 1.78, margin: '0 0 2.5rem' }}>
            {t('home.hero.desc')}
          </p>
          <div style={{ display: 'flex', gap: '1.2rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <a href="#studies" className="aecy-btn-primary" style={{
              fontFamily: f.mono, fontSize: '0.72rem', letterSpacing: '0.12em', textTransform: 'uppercase',
              background: 'var(--aecy-accent)', color: '#fff', padding: '0.85rem 2rem',
              borderRadius: 4, textDecoration: 'none', fontWeight: 700, transition: 'background 0.2s, transform 0.15s',
            }}>
              {t('home.hero.cta_primary')}
            </a>
            <a href="#about" className="aecy-btn-ghost" style={{
              fontFamily: f.mono, fontSize: '0.72rem', letterSpacing: '0.12em', textTransform: 'uppercase',
              color: 'var(--aecy-gray)', textDecoration: 'none', paddingBottom: '0.3rem',
              borderBottom: '1px solid rgba(107,114,128,0.4)', transition: 'color 0.2s',
            }}>
              {t('home.hero.cta_secondary')}
            </a>
          </div>
        </motion.div>

        {/* Right image */}
        <div className="aecy-hero-img" style={{ position: 'relative', overflow: 'hidden' }}>
          <img
            src="/aerocyclingProject-home-1.jpeg"
            alt="Ciclista en velódromo"
            style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'right', filter: 'grayscale(50%) contrast(1.05)', opacity: 0.5 }}
          />
          <div style={{
            position: 'absolute', inset: 0,
            background: 'linear-gradient(to right, var(--aecy-black) 0%, rgba(13,15,18,0.1) 65%)',
          }} />
        </div>
      </Box>

      {/* ── TICKER ────────────────────────────────────────────── */}
      <div className="aecy-ticker-wrap" style={{ background: 'var(--aecy-accent)', padding: '0.75rem 0' }}>
        <motion.div className="aecy-ticker-inner" animate={{ x: "-50%" }} transition={{ duration: 20, repeat: Infinity, ease: "linear" }}>
          {[...ticker, ...ticker, ...ticker, ...ticker].map((item, i) => (
            <React.Fragment key={i}>
              <span style={{ fontFamily: f.mono, fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#fff' }}>{item}</span>
              <span style={{ color: 'rgba(255,255,255,0.35)' }}>—</span>
            </React.Fragment>
          ))}
        </motion.div>
      </div>

      {/* ── 01 BENEFITS ───────────────────────────────────────── */}
      <motion.div id="benefits" className="aecy-section aecy-first-section" style={section('var(--aecy-surface)')} initial={{ opacity: 0, y: 50 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, type: "spring", stiffness: 50 }} viewport={{ once: true }}>
        <div className="aecy-two-col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4rem', alignItems: 'end', marginBottom: '4rem' }}>
          <div>
            <Tag>01 — {t('home.benefits.section_tag')}</Tag>
            <Title>{t('home.benefits.section_title')}</Title>
          </div>
          <p style={{ color: 'var(--aecy-white-dim)', fontSize: '1rem', lineHeight: 1.8, margin: 0 }}>
            {t('home.benefits.section_intro')}
          </p>
        </div>
        <motion.div className="aecy-three-col" style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '1px', background: 'var(--aecy-border)' }} variants={benefitContainerVariants} initial="hidden" whileInView="visible" viewport={{ once: true }}>
          {benefits.map((b) => (
            <motion.div key={b.title} className="aecy-benefit-item" style={{ padding: '2.5rem 2rem' }} variants={benefitItemVariants}>
              <div style={{ width: '2.8rem', height: '2.8rem', border: '1px solid var(--aecy-border-acc)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1.4rem', fontSize: '1.1rem', background: 'var(--aecy-accent-dim)' }}>{b.icon}</div>
              <h3 style={{ fontWeight: 500, fontSize: '1rem', margin: '0 0 0.5rem', color: 'var(--aecy-white)' }}>{b.title}</h3>
              <p style={{ fontSize: '0.88rem', color: 'var(--aecy-white-dim)', lineHeight: 1.75, margin: 0 }}>{b.desc}</p>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>

      {/* ── 02 ABOUT ──────────────────────────────────────────── */}
      <motion.div id="about" className="aecy-section" style={section('var(--aecy-black)')} initial={{ opacity: 0, y: 50 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }} viewport={{ once: true }}>
        <div className="aecy-two-col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6rem', alignItems: 'center' }}>
          <div>
            <Tag>02 — {t('home.about.section_tag')}</Tag>
            <Title>{t('home.about.section_title')}</Title>
            <div className="aecy-stats-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginTop: '3rem' }}>
              <motion.div variants={statContainerVariants} initial="hidden" whileInView="visible" viewport={{ once: true }}>
                {stats.map((s) => (
                  <motion.div key={s.label} className="aecy-stat-box" style={{ border: '1px solid var(--aecy-border)', background: 'var(--aecy-surface)', padding: '1.4rem', borderRadius: 6, transition: 'border-color 0.2s' }} variants={statItemVariants}>
                    <div style={{ fontFamily: f.display, fontSize: '3rem', color: 'var(--aecy-accent)', lineHeight: 1, marginBottom: '0.4rem' }}>{s.num}</div>
                    <div style={{ fontFamily: f.mono, fontSize: '0.62rem', letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--aecy-gray)' }}>{s.label}</div>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </div>
          <div>
            <p style={{ color: 'var(--aecy-white-dim)', lineHeight: 1.8, fontSize: '1rem', margin: '0 0 1.2rem' }}>
              {t('home.about.p1_pre')}<strong style={{ color: 'var(--aecy-white)', fontWeight: 500 }}>Pablo Díez</strong>{t('home.about.p1_mid')}<strong style={{ color: 'var(--aecy-white)', fontWeight: 500 }}>Víctor José Barneto</strong>{t('home.about.p1_post')}
            </p>
            <p style={{ color: 'var(--aecy-white-dim)', lineHeight: 1.8, fontSize: '1rem', margin: '0 0 2rem' }}>
              {t('home.about.p2')}
            </p>
            <div className="aecy-team-row" style={{ display: 'flex', gap: '1rem' }}>
              {[
                { name: 'Pablo Díez',          role: t('home.about.team.pablo') },
                { name: 'Víctor José Barneto',  role: t('home.about.team.victor') },
              ].map((p) => (
                <div key={p.name} className="aecy-team-card" style={{ flex: 1, background: 'var(--aecy-surface-2)', border: '1px solid var(--aecy-border)', borderRadius: 6, padding: '1.3rem', transition: 'border-color 0.2s' }}>
                  <div style={{ fontWeight: 500, fontSize: '0.95rem', marginBottom: '0.3rem', color: 'var(--aecy-white)' }}>{p.name}</div>
                  <div style={{ fontFamily: f.mono, fontSize: '0.6rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--aecy-accent)' }}>{p.role}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </motion.div>

      {/* ── 03 HOW ────────────────────────────────────────────── */}
      <motion.div id="how" className="aecy-section" style={section('var(--aecy-surface)')} initial={{ opacity: 0, x: -50 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.4 }} viewport={{ once: true }}>
        <div className="aecy-how-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6rem', alignItems: 'center' }}>
          <div>
            <Tag>03 — {t('home.how.section_tag')}</Tag>
            <Title>{t('home.how.section_title')}</Title>
            <motion.div style={{ marginTop: '3rem' }} variants={stepContainerVariants} initial="hidden" whileInView="visible" viewport={{ once: true }}>
              {steps.map((s, i) => (
                <motion.div key={s.title} style={{ display: 'grid', gridTemplateColumns: '3rem 1fr', gap: '1.5rem', marginBottom: '2.2rem', alignItems: 'start' }} variants={stepItemVariants}>
                  <div style={{ fontFamily: f.display, fontSize: '2.2rem', color: 'rgba(59,130,246,0.2)', lineHeight: 1 }}>{String(i + 1).padStart(2, '0')}</div>
                  <div>
                    <h4 style={{ fontWeight: 500, margin: '0 0 0.35rem', fontSize: '0.97rem', color: 'var(--aecy-white)' }}>{s.title}</h4>
                    <p style={{ fontSize: '0.88rem', color: 'var(--aecy-white-dim)', lineHeight: 1.75, margin: 0 }}>{s.desc}</p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
          <div style={{ position: 'relative', border: '1px solid var(--aecy-border)', borderRadius: 8, overflow: 'hidden', aspectRatio: '2/1' }}>
            <img
              src="/aerocyclingProject-home-2.jpeg"
              alt="Análisis biomecánico"
              style={{ width: '100%', height: '100%', objectFit: 'cover', filter: 'grayscale(50%) contrast(1.05)', opacity: 0.6 }}
            />
            <div style={{ position: 'absolute', bottom: '1.5rem', left: '1.5rem', right: '1.5rem', background: 'rgba(13,15,18,0.88)', backdropFilter: 'blur(8px)', border: '1px solid var(--aecy-border-acc)', borderRadius: 6, padding: '0.9rem 1.1rem' }}>
              <p style={{ fontFamily: f.mono, fontSize: '0.65rem', letterSpacing: '0.1em', color: 'var(--aecy-white-dim)', margin: 0 }}>{t('home.how.image_caption')}</p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* ── 04 STUDIES ────────────────────────────────────────── */}
      <motion.div id="studies" className="aecy-section" style={section('var(--aecy-black)')} initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.6 }} viewport={{ once: true }}>
        <div className="aecy-studies-hdr" style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: '2rem', marginBottom: '4rem', flexWrap: 'wrap' }}>
          <div>
            <Tag>04 — {t('home.studies.section_tag')}</Tag>
            <Title>{t('home.studies.section_title')}</Title>
          </div>
          <p style={{ fontFamily: f.mono, fontSize: '0.66rem', letterSpacing: '0.09em', color: 'var(--aecy-accent)', textAlign: 'right', lineHeight: 1.6, maxWidth: 280, margin: 0 }}>
            ★ {t('home.studies.note')}
          </p>
        </div>
        <div className="aecy-three-col" style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '1.2rem', marginBottom: '2.5rem' }}>
          {studies.map((s) => (
            <div key={s.type} className="aecy-study-card" style={{ background: s.featured ? 'var(--aecy-surface-2)' : 'var(--aecy-surface)', border: s.featured ? '1px solid rgba(59,130,246,0.4)' : '1px solid var(--aecy-border)', borderRadius: 8, padding: '2.2rem 2rem', display: 'flex', flexDirection: 'column', position: 'relative', transition: 'border-color 0.25s' }}>
              {s.badge && <div style={{ position: 'absolute', top: '1.2rem', right: '1.2rem', fontFamily: f.mono, fontSize: '0.58rem', letterSpacing: '0.14em', textTransform: 'uppercase', background: 'var(--aecy-accent)', color: '#fff', padding: '0.28rem 0.65rem', borderRadius: 3, fontWeight: 700 }}>{s.badge}</div>}
              <div style={{ fontFamily: f.mono, fontSize: '0.6rem', letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--aecy-gray)', marginBottom: '0.8rem' }}>{s.type}</div>
              <h3 style={{ fontFamily: f.display, fontSize: '1.9rem', lineHeight: 1.1, margin: '0 0 1.2rem', color: 'var(--aecy-white)' }}>
                {s.name}{s.nameAccent && <> <span style={{ color: 'var(--aecy-accent)' }}>{s.nameAccent}</span></>}
              </h3>
              <p style={{ fontFamily: f.mono, fontSize: '0.67rem', color: 'var(--aecy-gray)', letterSpacing: '0.09em', margin: '0 0 1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--aecy-accent)', flexShrink: 0, display: 'inline-block' }} />{s.duration}
              </p>
              <div style={{ marginTop: 'auto' }}>
                {s.prices.map((pr) => (
                  <div key={pr.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '0.65rem 0', borderTop: '1px solid var(--aecy-border)' }}>
                    <span style={{ color: 'var(--aecy-white-dim)', fontSize: '0.9rem' }}>{pr.label}</span>
                    <span style={{ fontFamily: f.display, fontSize: '1.7rem', color: 'var(--aecy-accent)' }}>{pr.value}</span>
                  </div>
                ))}
                {s.note && <div style={{ borderTop: '1px solid var(--aecy-border)', padding: '0.65rem 0' }}><span style={{ fontSize: '0.78rem', color: 'rgba(232,234,240,0.3)' }}>{s.note}</span></div>}
              </div>
            </div>
          ))}
        </div>
        {/* Grupo */}
        <div style={{ background: 'var(--aecy-accent-dim)', border: '1px solid var(--aecy-border-acc)', borderRadius: 8, padding: '1.5rem 2rem' }}>
          <div style={{ fontFamily: f.mono, fontSize: '0.66rem', letterSpacing: '0.15em', textTransform: 'uppercase', color: 'var(--aecy-accent)', marginBottom: '1rem' }}>★ {t('home.studies.group_title')}</div>
          <div className="aecy-group-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
            {[
              { label: `${t('home.studies.biomech.name')} · ${t('home.studies.one_bike')}`,  price: '260€' },
              { label: `${t('home.studies.biomech.name')} · ${t('home.studies.two_bikes')}`, price: '350€' },
            ].map((g) => (
              <div key={g.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '0.5rem 0', borderBottom: '1px solid rgba(255,255,255,0.04)', fontSize: '0.88rem' }}>
                <span style={{ color: 'var(--aecy-white-dim)' }}>{g.label}</span>
                <span style={{ fontFamily: f.mono, fontSize: '0.82rem', color: 'var(--aecy-white)' }}>{g.price}</span>
              </div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* ── 05 COLLABORATORS ──────────────────────────────────── */}
      <motion.div className="aecy-section" style={{ ...section('var(--aecy-surface)'), textAlign: 'center' }} initial={{ opacity: 0, y: -50 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.8 }} viewport={{ once: true }}>
        <Tag center>05 — {t('home.collab.section_tag')}</Tag>
        <Title center>{t('home.collab.section_title')}</Title>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 'clamp(2rem,4vw,4rem)', flexWrap: 'wrap', marginTop: '3rem' }}>
          {['Universidad San Jorge', 'Mntoor', 'Trek Zaragoza', 'Bikone'].map((c) => (
            <span key={c} className="aecy-collab-name" style={{ fontSize: 'clamp(1.2rem,2vw,1.5rem)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>{c}</span>
          ))}
        </div>
      </motion.div>

      {/* ── 06 CONTACT ────────────────────────────────────────── */}
      <motion.div id="contact" className="aecy-section" style={section('var(--aecy-black)')} initial={{ opacity: 0, x: 50 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 1.0 }} viewport={{ once: true }}>
        <div className="aecy-contact-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6rem', alignItems: 'center' }}>
          <div>
            <Tag>06 — {t('home.contact.section_tag')}</Tag>
            <Title>{t('home.contact.section_title')}</Title>
            <p style={{ fontSize: '1rem', color: 'var(--aecy-white-dim)', lineHeight: 1.78, margin: 0 }}>{t('home.contact.desc')}</p>
            <div style={{ marginTop: '2.5rem', display: 'flex', flexDirection: 'column', gap: '0.9rem' }}>
              {[
                { icon: '📞', label: '+34 687 38 93 68',       href: 'tel:+34687389368' },
                { icon: '✉️', label: 'aecy.project@gmail.com', href: 'mailto:aecy.project@gmail.com' },
                { icon: '📸', label: '@aecy_project',           href: 'https://instagram.com/aecy_project' },
              ].map((l) => (
                <a key={l.label} href={l.href} className="aecy-contact-link" style={{ display: 'flex', alignItems: 'center', gap: '1rem', textDecoration: 'none', color: 'var(--aecy-white)', fontSize: '1rem', fontWeight: 500, transition: 'color 0.2s' }}>
                  <div style={{ width: '2.4rem', height: '2.4rem', borderRadius: 6, background: 'var(--aecy-surface-2)', border: '1px solid var(--aecy-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.95rem', flexShrink: 0 }}>{l.icon}</div>
                  {l.label}
                </a>
              ))}
            </div>
          </div>
          <div>
            <div style={{ background: 'var(--aecy-surface)', border: '1px solid var(--aecy-border-acc)', borderRadius: 10, padding: '3rem' }}>
              <h3 style={{ fontFamily: f.display, fontSize: '2.2rem', color: 'var(--aecy-accent)', margin: '0 0 0.8rem' }}>linktr.ee/aecypro</h3>
              <p style={{ fontSize: '0.9rem', color: 'var(--aecy-white-dim)', margin: '0 0 2rem', lineHeight: 1.7 }}>{t('home.contact.linktree_desc')}</p>
              <a href="https://linktr.ee/aecypro" target="_blank" rel="noreferrer" className="aecy-cta-btn" style={{ display: 'inline-block', fontFamily: f.mono, fontSize: '0.72rem', letterSpacing: '0.12em', textTransform: 'uppercase', background: 'var(--aecy-accent)', color: '#fff', padding: '0.85rem 2rem', borderRadius: 4, textDecoration: 'none', fontWeight: 700, transition: 'background 0.2s' }}>
                {t('home.contact.linktree_cta')} →
              </a>
            </div>
          </div>
        </div>
      </motion.div>

      <Footer />

    </Box>
  );
};

export default Home;